import { SaveIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import { SettingsPageContainer, SettingsSection } from "../settings/settingsLayout";
import {
  HOSTED_MEMORY_LIMITS,
  HostedApiError,
  resolveMemoryReplacementOperation,
  type HostedMemoryDocument,
  type HostedMemoryScope,
} from "../../hosted/api";
import {
  MEMORY_SCOPE_DESCRIPTIONS,
  MEMORY_SCOPE_LABELS,
  resolveMemoryEditorState,
  resolveProjectMemoryForSelection,
  type ScopedProjectMemory,
} from "../../hosted/studioPresentation";
import { useHostedAccountEpoch, useHostedProjectStore } from "../../hosted/hostedProjectStore";
import { useHostedProjects } from "../../hosted/useHostedProjects";
import { hostedApiErrorMessage, useHostedApiClient } from "../../hosted/useHostedApiClient";

/**
 * Bounded memory editor for USER and PROJECT stores.
 *
 * Writes are explicit and immediate: a save persists now and affects the next
 * thread, which is what keeps prompt caching predictable. Capacity is enforced
 * in the UI and again by the Worker, and an over-capacity save is refused with
 * the current usage so the text can be consolidated deliberately.
 */
export function HostedMemorySettingsPanel() {
  const { projects, status } = useHostedProjects();
  const selectedProjectId = useHostedProjectStore((state) => state.selectedProjectId);
  const activeProject = projects.find((project) => project.id === selectedProjectId) ?? null;
  const client = useHostedApiClient();
  const accountEpoch = useHostedAccountEpoch();

  const [userDocument, setUserDocument] = useState<HostedMemoryDocument | null>(null);
  // Kept with its project id: a save is a substring operation keyed on the text
  // being displayed, so a document read for a different project must never be
  // the one an editor saves against.
  const [projectDocument, setProjectDocument] =
    useState<ScopedProjectMemory<HostedMemoryDocument> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const projectId = activeProject?.id ?? null;

  // The two scopes load independently, keyed on what each actually depends on:
  // user memory is account-scoped and readable before any project exists, while
  // project memory follows the selection. Loading them together would clear and
  // refetch the user document on every project switch, flashing that editor
  // empty for no reason.
  useEffect(() => {
    setUserDocument(null);
    let current = true;
    void (async () => {
      try {
        const user = await client.readMemory({ scope: "user", projectId: null });
        if (!current) {
          return;
        }
        setUserDocument(user);
        setLoadError(null);
      } catch (error) {
        if (current) {
          setLoadError(hostedApiErrorMessage(error));
        }
      }
    })();
    return () => {
      current = false;
    };
  }, [accountEpoch, client]);

  useEffect(() => {
    if (!projectId) {
      setProjectDocument(null);
      return;
    }
    let current = true;
    void (async () => {
      try {
        const project = await client.readMemory({ scope: "project", projectId });
        if (!current) {
          return;
        }
        // Stamped with the project it was read for, so a read that resolves after
        // the selection moved is ignored rather than shown under the new project.
        setProjectDocument({ projectId, document: project });
      } catch (error) {
        if (current) {
          setLoadError(hostedApiErrorMessage(error));
        }
      }
    })();
    return () => {
      current = false;
    };
  }, [accountEpoch, client, projectId]);

  const activeProjectDocument = resolveProjectMemoryForSelection({
    loaded: projectDocument,
    selectedProjectId: projectId,
  });

  const saveScope = useCallback(
    async (scope: HostedMemoryScope, nextText: string) => {
      if (scope === "project" && projectDocument?.projectId !== projectId) {
        // The editor is not showing this project's document yet, so there is no
        // text to key a substring operation on.
        throw new HostedApiError({
          code: "invalid_request",
          message: "Project memory is still loading. Try again in a moment.",
          status: null,
        });
      }
      const previousText =
        (scope === "user" ? userDocument?.text : projectDocument?.document.text) ?? "";
      const operation = resolveMemoryReplacementOperation({
        previous: previousText,
        next: nextText,
      });
      if (!operation) {
        return;
      }
      const saved = await client.applyMemoryOperation({
        scope,
        projectId: scope === "project" ? projectId : null,
        operation,
      });
      if (scope === "user") {
        setUserDocument(saved);
        return;
      }
      if (projectId) {
        setProjectDocument({ projectId, document: saved });
      }
    },
    [client, projectDocument, projectId, userDocument],
  );

  return (
    <SettingsPageContainer>
      <SettingsSection title="Memory">
        <div className="space-y-3 px-3 sm:px-4">
          <p className="max-w-xl text-[13px] leading-[1.45] text-muted-foreground/80">
            Memory is injected as a frozen snapshot when a thread starts. Saving persists
            immediately and applies to the next thread.
          </p>

          {loadError ? (
            <p className="text-xs text-destructive-foreground" role="alert">
              {loadError}
            </p>
          ) : null}

          <MemoryScopeEditor
            scope="user"
            document={userDocument}
            disabled={false}
            onSave={saveScope}
          />

          {activeProject ? (
            <p className="text-xs text-muted-foreground/70">
              Project memory applies to{" "}
              <span className="text-foreground">{activeProject.name}</span>.
            </p>
          ) : status === "ready" ? (
            <p className="text-xs text-muted-foreground/70">
              Create a project before editing project memory.
            </p>
          ) : null}

          <MemoryScopeEditor
            scope="project"
            document={activeProjectDocument}
            // Editing before this project's document has loaded would key a
            // substring save on text the server does not have.
            disabled={projectId === null || activeProjectDocument === null}
            onSave={saveScope}
          />
        </div>
      </SettingsSection>
    </SettingsPageContainer>
  );
}

function MemoryScopeEditor({
  scope,
  document,
  disabled,
  onSave,
}: {
  readonly scope: HostedMemoryScope;
  readonly document: HostedMemoryDocument | null;
  readonly disabled: boolean;
  readonly onSave: (scope: HostedMemoryScope, text: string) => Promise<void>;
}) {
  const savedText = document?.text ?? "";
  const limit = document?.limit ?? HOSTED_MEMORY_LIMITS[scope];
  const [draft, setDraft] = useState(savedText);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Reset the draft when the server text changes underneath (initial load, or a
  // successful save that normalized the text). Local edits win until then.
  useEffect(() => {
    setDraft(savedText);
    setSaveError(null);
  }, [savedText]);

  const editor = resolveMemoryEditorState({ draft, saved: savedText, limit, saving: isSaving });
  const inputId = `hosted-memory-${scope}`;
  const countId = `${inputId}-count`;
  const errorId = `${inputId}-error`;
  const visibleError = saveError ?? editor.error;

  const save = () => {
    if (!editor.canSave) return;
    setIsSaving(true);
    setSaveError(null);
    void (async () => {
      try {
        await onSave(scope, draft);
        setSavedAt(new Date().toISOString());
      } catch (error) {
        setSaveError(hostedApiErrorMessage(error));
      } finally {
        setIsSaving(false);
      }
    })();
  };

  return (
    <div className="rounded-xl border border-border/70 bg-card/25 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <label className="text-sm font-medium text-foreground" htmlFor={inputId}>
            {MEMORY_SCOPE_LABELS[scope]}
          </label>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground/80">
            {MEMORY_SCOPE_DESCRIPTIONS[scope]}
          </p>
        </div>
        <span
          aria-live="polite"
          className={cn(
            "shrink-0 font-mono text-xs",
            editor.overCapacity ? "text-destructive-foreground" : "text-muted-foreground/70",
          )}
          id={countId}
        >
          {editor.used}/{editor.limit}
        </span>
      </div>

      <div className="mt-3">
        <Textarea
          aria-describedby={visibleError ? `${countId} ${errorId}` : countId}
          aria-invalid={editor.overCapacity || saveError !== null || undefined}
          aria-label={MEMORY_SCOPE_LABELS[scope]}
          disabled={disabled || isSaving}
          id={inputId}
          placeholder={
            scope === "user"
              ? "How you want agents to communicate with you."
              : "Conventions, architecture, current milestone, Studio facts."
          }
          rows={6}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (saveError) setSaveError(null);
          }}
        />
      </div>

      {visibleError ? (
        <p className="mt-2 text-xs text-destructive-foreground" id={errorId} role="alert">
          {visibleError}
        </p>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <Button disabled={!editor.canSave || disabled} size="xs" onClick={save}>
          {isSaving ? <Spinner className="size-3" /> : <SaveIcon className="size-3" />}
          {isSaving ? "Saving…" : "Save"}
        </Button>
        {editor.dirty && !editor.overCapacity ? (
          <span className="text-xs text-muted-foreground/70">Unsaved changes.</span>
        ) : savedAt ? (
          <span className="text-xs text-muted-foreground/70">
            Saved. Applies to the next thread.
          </span>
        ) : null}
      </div>
    </div>
  );
}
